import { Check, Copy } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/cn';

/** Read-only payload viewer: strings render as text (prompts stay readable), everything else as JSON. */
export const JsonView = ({ value, className, empty = 'Not recorded' }: { value: unknown; className?: string; empty?: string }) => {
  const [copied, setCopied] = useState(false);
  if (value === null || value === undefined) return <p className="text-[13px] text-ink-3 italic">{empty}</p>;
  const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  return (
    <div className={cn('group relative', className)}>
      <button
        className="absolute top-1.5 right-1.5 rounded border border-line bg-panel p-1 text-ink-3 opacity-0 transition-opacity group-hover:opacity-100 hover:text-ink"
        onClick={() => {
          void navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
          });
        }}
        title="Copy"
      >
        {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
      </button>
      <pre className="max-h-[480px] overflow-auto rounded-md border border-line bg-line-2/60 p-3 leading-relaxed break-words whitespace-pre-wrap">{text}</pre>
    </div>
  );
};

/** Flatten provider content parts ([{type:'text', text}, {type:'file', …}]) into readable text. */
const partsToText = (content: unknown): string | null => {
  if (typeof content === 'string') return content;
  if (!Array.isArray(content)) return null;
  const out: string[] = [];
  for (const p of content as Array<Record<string, any>>) {
    if (p?.type === 'text' || p?.type === 'input_text' || p?.type === 'output_text') out.push(String(p.text ?? ''));
    else if (p?.type === 'file' || p?.type === 'input_file') out.push(`[file: ${p.file?.filename ?? p.filename ?? 'attachment'}]`);
    else if (p?.type === 'image_url' || p?.type === 'input_image') out.push('[image]');
    else return null;
  }
  return out.join('\n');
};

/** The model's reply from a raw provider response (chat completion) or engine output items. */
export const ResponseView = ({ value }: { value: unknown }) => {
  const first = Array.isArray(value) ? (value[0] as Record<string, any> | undefined) : undefined;
  const choice = first?.choices?.[0];
  if (choice?.message) {
    const m = choice.message;
    return (
      <div className="flex flex-col gap-2">
        {m.content && <pre className="max-h-[360px] overflow-auto rounded-md border border-line p-3 break-words whitespace-pre-wrap">{m.content}</pre>}
        {m.tool_calls && <JsonView value={m.tool_calls.map((t: any) => ({ tool: t.function?.name, arguments: tryJson(t.function?.arguments) }))} />}
        {m.refusal && <pre className="rounded-md border border-bad/20 bg-bad-soft p-3 text-bad">{m.refusal}</pre>}
        <p className="text-xs text-ink-3">finish_reason: {choice.finish_reason}</p>
      </div>
    );
  }
  return <JsonView value={value} />;
};

const tryJson = (s: unknown) => {
  if (typeof s !== 'string') return s;
  try {
    return JSON.parse(s);
  } catch {
    return s;
  }
};

/** Chat-style rendering for model input arrays ([{role, content}]); falls back to JSON. */
export const MessagesView = ({ value }: { value: unknown }) => {
  const list = Array.isArray(value) ? (value as Array<Record<string, any>>) : null;
  const flat = list?.every((m) => m && typeof m === 'object' && 'role' in m);
  if (!flat) return <JsonView value={value} />;
  return (
    <div className="flex flex-col gap-2">
      {list!.map((m, i) => (
        <div key={i} className="rounded-md border border-line">
          <div className="border-b border-line bg-line-2/60 px-3 py-1 text-[11px] font-semibold tracking-wide text-ink-2 uppercase">
            {m.role}
            {m.tool_call_id ? ` · ${m.tool_call_id}` : ''}
          </div>
          <div className="p-3">
            {partsToText(m.content) !== null ? (
              <pre className="max-h-[360px] overflow-auto break-words whitespace-pre-wrap">{partsToText(m.content)}</pre>
            ) : m.content ? (
              <JsonView value={m.content} />
            ) : null}
            {m.tool_calls && <JsonView value={m.tool_calls.map((t: any) => ({ tool: t.function?.name, arguments: tryJson(t.function?.arguments) }))} className="mt-2" />}
          </div>
        </div>
      ))}
    </div>
  );
};
